import { focusFirst, lockKeyboardFocus } from '@disy/lib/event-util';

import './dialog.css';

/**
 * @type R the type of the return value
 */
export class Dialog<R = undefined> extends HTMLElement {
  _focusOnCloseElement?: HTMLElement;
  _returnValue?: R;

  protected dialog;

  constructor() {
    super();
    this.dialog = document.createElement('dialog') as HTMLDialogElement;
    this.dialog.addEventListener('cancel', (event) => this._onCancel(event));
    this.dialog.addEventListener('close', (event) => this._onClose(event));
    this.dialog.addEventListener('click', (event) => this._onClick(event));
    this.dialog.addEventListener('keydown', lockKeyboardFocus);
  }

  connectedCallback() {
    this.appendChild(this.dialog);
  }

  disconnectedCallback() {
    // does nothing by default
  }

  get open() {
    return this.dialog.open;
  }

  set returnValue(value: R | undefined) {
    this._returnValue = value;
    this.dialog.returnValue = '';
  }

  get returnValue(): R {
    // if this.dialog.returnValue has a value, we know that T has been instantiated as string
    return (this.dialog.returnValue as unknown as R) || this._returnValue!;
  }

  cancel() {
    const cancelEvent = new CustomEvent('cancel', { cancelable: true });
    if (this.dispatchEvent(cancelEvent)) {
      this.close();
    }
  }

  close(returnValue?: R) {
    this.returnValue = returnValue;
    this.dialog.close();
  }

  /**
   * Don't use.
   *
   * @deprecated `remove()` is not actually part of the `Dialog` API. Use {@link #close} or {@link #cancel} instead.
   */
  override remove() {
    super.remove();
  }

  show(): Promise<R> {
    this.dialog.show();
    focusFirst(this.dialog);

    return new Promise((resolve) => {
      this.addEventListener('close', () => {
        resolve(this.returnValue);
      });
    });
  }

  showModal(): Promise<R> {
    if (document.activeElement instanceof HTMLElement) {
      this._focusOnCloseElement = document.activeElement;
    }

    this.dialog.showModal();
    focusFirst(this.dialog);

    return new Promise((resolve) => {
      this.addEventListener('close', () => {
        resolve(this.returnValue);
      });
    });
  }

  _onCancel(event: Event) {
    if (event.target === this.dialog) {
      event.preventDefault();
      this.cancel();
    }
  }

  _onClose(event: Event) {
    if (event.target === this.dialog) {
      this._focusOnCloseElement?.focus();
      this._focusOnCloseElement = undefined;

      this.dispatchEvent(new CustomEvent('close'));
      this.returnValue = undefined; // reset returnValue after listeners consumed it
    }
  }

  _onClick(event: MouseEvent) {
    const clickIsOnCancelButton = (event.target as HTMLElement).closest('.cancel') != null;
    if (clickIsOnCancelButton) {
      this.cancel();
    }
  }
}
